import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ExpertDoc, Resume } from 'packages/types'
import { i18n } from 'builder/utils/i18n'
import { useDocument } from 'builder/hooks/useDocument'
import { useGetResume } from 'builder/hooks/useGetResume'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import Icon24 from 'builder/components/Icon'
import { ButtonTheme } from 'builder/components/Button'
import { formatDate } from 'builder/utils/formatDate'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'
import FieldSpinnerLoader from 'builder/views/ResumeDistributionView/components/FormLayout/FieldSpinnerLoader'

import {
  DESKTOP_PREVIEW_WIDTH,
  PHONE_PREVIEW_WIDTH,
} from 'builder/components/Panel/Document/constants'
import { DocumentTypes } from 'builder/modules/constants'
import { StartCompleteScreenRoute } from '../../constants'

import {
  ButtonsContainer,
  ChangeResumeButton,
  DocumentPreviewContainer,
  Placeholder,
  SpinnerContainer,
  PreviewButton,
  PreviewDocument,
  ResumeSelectionContainer,
  RightBottonShadow,
  SelectLeft,
  SelectRight,
  TextContainer,
  Title,
  UploadDate,
} from './styles'

type Props = {
  isLoading?: boolean
  upladedAt?: string
  resumeUrl?: string
  showChangeButton?: boolean
  selectedResumeId: number | null
  selectedResumeType: DocumentTypes | null
  onClickPreview?: () => void
  handleBuilderResumeChange: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const TRANSLATION = 'builder.auto_apply.form.personal_information'

export const ResumePreview = ({
  isLoading,
  upladedAt,
  resumeUrl,
  showChangeButton = true,
  selectedResumeId,
  selectedResumeType,
  onClickPreview,
  handleBuilderResumeChange,
}: Props) => {
  const { isPhone } = useMediaQueries()
  const navigate = useNavigate()
  const { features } = useFeaturesConfig()

  const getResumeId = () => {
    const isResume = selectedResumeType === DocumentTypes.resume
    const isTrDocument = selectedResumeType === DocumentTypes.trDocument

    return {
      [DocumentTypes.resume]: isResume ? selectedResumeId : null,
      [DocumentTypes.trDocument]: isTrDocument ? selectedResumeId : null,
    }
  }

  const resume = useGetResume({ id: getResumeId()[DocumentTypes.resume], cacheTime: 1 })
  const document = useDocument(getResumeId()[DocumentTypes.trDocument])
  const selectedResume = resume?.data || document
  const isCoverLetter = features.aaTailoring === 'v1' && document?.doc_type === 'cover_letter'

  const previewUrl = useCallback(
    (document: Resume | ExpertDoc) => {
      return formatDocumentPreviewUrl(document, {
        size: isPhone ? PHONE_PREVIEW_WIDTH : DESKTOP_PREVIEW_WIDTH,
      })
    },
    [isPhone],
  )

  const getThumbnailImg = () => {
    if (selectedResume?.type === DocumentTypes.trDocument && selectedResume?.thumbnail?.url) {
      return selectedResume?.thumbnail?.url
    }
    return selectedResume ? previewUrl(selectedResume) : null
  }

  const getDocumentName = () => {
    if (document) {
      return document?.title || i18n.t('builder.dashboard.untitled')
    }
    if (resume.data) {
      const { name, jobTitle } = resume.data
      return jobTitle ? `${name}, ${jobTitle}` : name
    }
    if (resumeUrl) {
      return resumeUrl.split('/').pop()
    }
    return i18n.t('builder.dashboard.untitled')
  }

  const formatUpdateDate = () => {
    const date = selectedResume?.updatedAt || selectedResume?.createdAt || new Date().toISOString()
    return upladedAt || formatDate(new Date(date), 'dd MMM, HH:mm')
  }

  const onPreview = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (onClickPreview) onClickPreview()
  }

  const navigateToResumeSelect = useCallback(() => {
    if (selectedResumeType === DocumentTypes.resume && selectedResumeId && resume.error) {
      navigate(`/auto-apply/${StartCompleteScreenRoute.RESUME_SELECT}`)
    }
  }, [navigate, resume.error, selectedResumeId, selectedResumeType])

  useEffect(() => {
    navigateToResumeSelect()
  }, [navigateToResumeSelect, resume])

  return (
    <ResumeSelectionContainer>
      <SelectLeft>
        <TextContainer>
          <Title>{getDocumentName()}</Title>
          <UploadDate>{i18n.t(`${TRANSLATION}.updated`, { date: formatUpdateDate() })}</UploadDate>
        </TextContainer>
        <ButtonsContainer>
          {selectedResumeId && !isLoading && (
            <PreviewButton onClick={onPreview}>
              <Icon24.View />
              {i18n.t(`${TRANSLATION}.preview.button`)}
            </PreviewButton>
          )}
          {showChangeButton && (
            <ChangeResumeButton
              theme={selectedResumeId ? ButtonTheme.ghost : ButtonTheme.secondary}
              onClick={handleBuilderResumeChange}
            >
              {isLoading ? (
                <SpinnerContainer>
                  <FieldSpinnerLoader />
                </SpinnerContainer>
              ) : (
                i18n.t(`${TRANSLATION}.${isCoverLetter ? 'change_cover_letter' : 'change_resume'}`)
              )}
            </ChangeResumeButton>
          )}
        </ButtonsContainer>
      </SelectLeft>
      <SelectRight>
        <DocumentPreviewContainer>
          {selectedResumeId ? (
            <PreviewDocument showUnavailablePreview={false} src={getThumbnailImg()} />
          ) : (
            <Placeholder />
          )}
          <RightBottonShadow />
        </DocumentPreviewContainer>
      </SelectRight>
    </ResumeSelectionContainer>
  )
}
